import React, { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import './EntityTile.css';

export type EntityIconVariant = 'purchase' | 'sale' | 'info' | 'success' | 'warning' | 'urgent';

export interface EntityTileProps {
  /** Icon element rendered in the tile header */
  icon: ReactNode;
  /** Color scheme for the icon background */
  iconVariant?: EntityIconVariant;
  /** Primary title text */
  title: string;
  /** Subtitle rendered below the title (e.g. parties with org links) */
  subtitle?: ReactNode;
  /** Secondary meta text (gray, small — e.g. product name, category) */
  meta?: string;
  /** Tag bubbles rendered left-aligned in the title row (status badges, percentages, etc.) */
  tags?: ReactNode;
  /**
   * Whether the tile can expand. When `false`, the tile renders as a static row:
   * no chevron, no button semantics (no `role`/`tabIndex`/keyboard handler), and
   * no accordion body. Defaults to `true` to preserve the expandable behaviour
   * every existing consumer relies on.
   */
  expandable?: boolean;
  /** Whether the tile is expanded */
  isExpanded?: boolean;
  /** Toggle expand handler */
  onToggle?: () => void;
  /** Even/odd row for alternating background */
  isEvenRow?: boolean;
  /** Optional class name */
  className?: string;
  /** Expanded body content — rendered inside the accordion area */
  children?: ReactNode;
}

/**
 * EntityTile — Generic expandable tile for any farm entity (contracts, animals, fields, etc.).
 * Follows the same visual pattern as EventTile: icon + title + tags header,
 * chevron toggle, and framer-motion accordion body.
 */
export const EntityTile: React.FC<EntityTileProps> = ({
  icon,
  iconVariant = 'info',
  title,
  subtitle,
  meta,
  tags,
  expandable = true,
  isExpanded = false,
  onToggle,
  isEvenRow = false,
  className = '',
  children,
}) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle?.();
    }
  };

  // Interactive (button) semantics only when the tile can expand. A static row
  // gets none of these — no role, no tabIndex, no click/keyboard toggle.
  const interactiveProps = expandable
    ? {
        onClick: onToggle,
        role: 'button' as const,
        tabIndex: 0,
        onKeyDown: handleKeyDown,
      }
    : {};

  return (
    <div
      className={`entity-tile ${isEvenRow ? 'entity-tile--even' : 'entity-tile--odd'} ${isExpanded ? 'entity-tile--expanded' : ''} ${className}`}
    >
      {/* Header — always visible */}
      <div
        className={`entity-tile__header ${isExpanded ? 'entity-tile__header--expanded' : ''} ${!expandable ? 'entity-tile__header--static' : ''}`}
        {...interactiveProps}
      >
        <div className="entity-tile__header-content">
          {/* Icon */}
          <div className={`entity-tile__icon entity-tile__icon--${iconVariant}`}>
            {icon}
          </div>

          {/* Main info */}
          <div className="entity-tile__info">
            <div className="entity-tile__title-row">
              {subtitle ? (
                <div className="entity-tile__title-stack">
                  <span className="entity-tile__title">{title}</span>
                  <span className="entity-tile__subtitle">{subtitle}</span>
                </div>
              ) : (
                <span className="entity-tile__title">{title}</span>
              )}
              {meta && (
                <span className="entity-tile__type">{meta}</span>
              )}
              {tags && (
                <div className="entity-tile__tags">
                  {tags}
                </div>
              )}
            </div>
          </div>

          {/* Chevron */}
          {expandable && (
            <ChevronRight
              className={`entity-tile__chevron ${isExpanded ? 'entity-tile__chevron--expanded' : ''}`}
            />
          )}
        </div>
      </div>

      {/* Expandable body */}
      {expandable && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{
            height: isExpanded ? 'auto' : 0,
            opacity: isExpanded ? 1 : 0,
          }}
          transition={{ duration: 0.2 }}
          className="entity-tile__body"
        >
          <div className="entity-tile__body-content">
            {children}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default EntityTile;
